import { CustomMenuItem } from '../../../@core/components/ui/menu/menu.component';
import { FILE_TYPE } from '../../../@core/models/enums';
import { Document, IAttachment, IUser } from '../../../@core/models/model';
import { DOCUMENT_MODEL, EMBEDDINGS_GENERATION_STATUS } from '../../../contract-management/models/contract-management.model';
import { CONNECTOR_PROVIDERS } from '../constants/tool.constants';
import { LLM_PROVIDERS, THREAD_STREAMING_STATUS } from './model';

export enum AGENT_MODE {
    ASK = 'ASK',
    AGENTIC = 'AGENTIC'
}

export enum CHECKPOINT_MODE {
    EDIT = 'EDIT',
    RETRY = 'RETRY',
}

export enum CREDIT_NUDGE_MODE {
    TO_AGENT = 'TO_AGENT',
    TO_ASK = 'TO_ASK',
}

export interface ChatMessagePayload {
    query: string
    provider: LLM_PROVIDERS
    attachments?: {
        _id: string
        docModel: DOCUMENT_MODEL
    }[]
    threadId?: string
    threadMetadataId?: string
    tools?: CustomMenuItem['selectionTree'][]
    messageId?: string  // For edit/retry functionality
    checkpointMode?: CHECKPOINT_MODE
    temp_id?: string;  // ✅ New field
    connectors?: CONNECTOR_PROVIDERS[]
    canvasId?: string;
    selectedCanvasText?: string;
    canvasName?: string;
    documentId?: string;
    comparisonId?: string;
    mode: AGENT_MODE;
}

export enum PLAN_FEEDBACK {
    APPROVE = 'APPROVE',
    REJECT = 'REJECT',
    /** User edited the plan in-place before proceeding. Requires editedPlan in body. */
    EDIT = 'EDIT',
    /** User sent freeform feedback for AI to regenerate the plan. Requires revisionFeedback in body. */
    REVISE = 'REVISE',
}

export enum FILE_GROUP {
    ATTACHED = 'ATTACHED',
    HELPER = 'HELPER',
    OUTPUT = 'OUTPUT',
    CANVAS = 'CANVAS',
}

export enum ATTACHMENT_STATUS {
    PENDING = 'PENDING',
    SENT = 'SENT'
}

export interface ChatAttachment {
    _id: string;
    docModel: DOCUMENT_MODEL;
    /** Original source document's id (e.g. the repository File) when this attachment is a sandbox copy of a linked file. */
    refFile?: string;
    refDocModel?: DOCUMENT_MODEL;
    name: string;
    type: FILE_TYPE;
    attachmentData?: IAttachment;
    status?: EMBEDDINGS_GENERATION_STATUS;
    group?: FILE_GROUP;
    /** Lifecycle status: PENDING until sent in a message, then SENT. */
    attachmentStatus?: ATTACHMENT_STATUS;
    category?: string;
    metadata?: Record<string, any>;
}

/** State to restore in ThreadInputComponent after file-upload thread creation. */
export interface RestoredInputState {
    attachments: ChatAttachment[] | null;
    query: string | null;
    mode: AGENT_MODE | null;
    provider?: LLM_PROVIDERS | null;
    /** Whether a file over the size limit was attached — restored so the warning banner survives navigation. */
    largeFileAttached?: boolean | null;
    /** Unsent draft prompt saved when the user navigated away before sending. */
    draftPrompt?: string | null;

    stagedFiles?: File[] | null;
}

export enum QueryOptions {
    ADD_PHOTOS_AND_FILES = 'ADD_PHOTOS_AND_FILES',
    IMAGE_ANALYSIS = 'IMAGE_ANALYSIS',
    DEEP_RESEARCH = 'DEEP_RESEARCH',
    STUDY_AND_LEARN = 'STUDY_AND_LEARN',
    KNOWLEDGE_BASE = 'KNOWLEDGE_BASE',
    PROJECTS = 'PROJECTS',
    WEB_SEARCH = 'WEB_SEARCH',
    USE_STYLES = 'USE_STYLES',
    PROJECT_FILES = 'PROJECT_FILES',
    PROJECT_KNOWLEDGE = 'PROJECT_KNOWLEDGE',
    WORKSPACE_KNOWLEDGE = 'WORKSPACE_KNOWLEDGE',
    GOOGLE_DRIVE_FILES = 'GOOGLE_DRIVE_FILES',
    CUSTOM_INSTRUCTIONS = 'CUSTOM_INSTRUCTIONS',
    CANVAS = 'CANVAS',
    LINKED_DOCS = 'LINKED_DOCS',
    COMPARE_DOCS = 'COMPARE_DOCS',
    KPIS = 'KPIs',
    FORM_QUESTIONS = 'FORM_QUESTIONS',
    MODE = 'MODE'
}

export enum CHAT_STYLES {
    NORMAL = 'Normal',
    LEARNING = 'Learning',
    CONCISE = 'Concise',
    FORMAL = 'Formal',
    CASUAL = 'Casual',
    EXPLANATORY = 'Explanatory',
}

export enum MESSAGE_FEEDBACK {
    LIKE = 'LIKE',
    DISLIKE = 'DISLIKE',
}

export interface IGeneralAgentChatResponse {
    status: THREAD_STREAMING_STATUS,
    threadId: string,
    threadMetadataId: string,
    messages: any[],

    // Retry/Edit fields
    isRetry?: boolean;
    editedMessageId?: string;
    editedMessageIndex?: number;
    clearFromIndex?: number;
    clearBeforeRetry?: boolean;

    // Action types
    action?: 'prepare_clear' | 'clear_and_prepare_retry' | 'retry_starting' | 'edited_message';
    keepMessagesBeforeIndex?: number;
    generated_files?: any[],

    // Provider info
    provider?: string;
    tokenCount?: number;
    input_tokens?: number;

    // Tool execution info
    tool_name?: string;
    tool_status?: 'started' | 'completed' | 'error';
    tool_id?: string; // Tool call ID for linking streaming content
    content?: string; // Streaming content for tool arguments (input_json_delta)
    file_path?: string; // File path for write_file tool
    error_message?: string;
}

export interface IMessageFeedback extends Document {
    threadId: string,

    messageId: string,
    feedback: MESSAGE_FEEDBACK,
    details: string,
    updatedBy: string | IUser,

    createdAt: Date,
    updatedAt: Date
}


export interface IMemoryContentItem {
    text: string;
    threadMetadataId: string;
    threadId: string;
    createdAt: string | null;  // Per-line created timestamp
}

export interface IAgentMemory {
    key: string;                      // S3 key (full path)
    filename: string;                 // Just the filename
    content: IMemoryContentItem[];    // Array of memory items with per-line thread association
    lastModified: Date;               // Last modification time
}

